import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAllTeams,
  fetchTeamById,
  createTeam,
  updateTeam,
  deleteTeam,
  addMemberToTeam,
  removeMemberFromTeam,
  fetchAllUsers,
} from './StaffDiscordApi';

// Interface for Discord team data
export interface Discord {
  id: string;
  slug: string;
  name: string;
  description: string;
  logoUpload: File | string;
  published: boolean;
  modMembers: string[];
  rank: number;
  inviteLink: string;
}

// More specific interface for the context type
interface DiscordContextType {
  discord: Discord[] | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  createDiscord: (teamData: FormData) => Promise<{ id: string }>;
  updateDiscord: (data: { id: string; teamData: Partial<Discord> }) => void;
  deleteDiscord: (id: string) => void;
  addMember: (teamId: string, userId: string) => void;
  removeMember: (teamId: string, userId: string) => void;
  fetchTeamById: (id: string) => Promise<Discord>;
  users: any[] | null;
  isLoadingUsers: boolean;
}

const DiscordContext = createContext<DiscordContextType | undefined>(undefined);

interface DiscordProviderProps {
  children: ReactNode;
}

export const DiscordProvider: React.FC<DiscordProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery<Discord[], Error>({
    queryKey: ['teams'],
    queryFn: fetchAllTeams,
  });

  const { data: users, isLoading: isLoadingUsers } = useQuery<any[], Error>({
    queryKey: ['users'],
    queryFn: fetchAllUsers,
  });

  const createDiscordMutation = useMutation({
    mutationFn: createTeam,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      return response;
    },
  });

  const updateDiscordMutation = useMutation({
    mutationFn: ({ id, teamData }: { id: string; teamData: Partial<Discord> }) => {
      const formData = new FormData();

      // Append all fields from teamData to FormData
      Object.entries(teamData).forEach(([key, value]) => {
        if (value !== undefined) {
          if (key === 'logoUpload' && value instanceof File) {
            formData.append(key, value);
          } else if (key === 'modMembers' && Array.isArray(value)) {
            value.forEach((memberId) => formData.append('modMembers[]', memberId));
          } else if (typeof value === 'boolean') {
            formData.append(key, value.toString());
          } else if (value !== null) {
            formData.append(key, String(value));
          }
        }
      });

      return updateTeam(id, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  const deleteDiscordMutation = useMutation({
    mutationFn: (id: string) => deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      addMemberToTeam(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      removeMemberFromTeam(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  const contextValue: DiscordContextType = {
    discord: data || null,
    isLoading,
    isError,
    error: error || null,
    createDiscord: (teamData) => createDiscordMutation.mutateAsync(teamData),
    updateDiscord: (data) => updateDiscordMutation.mutate(data),
    deleteDiscord: (id) => deleteDiscordMutation.mutate(id),
    addMember: (teamId, userId) => addMemberMutation.mutate({ teamId, userId }),
    removeMember: (teamId, userId) => removeMemberMutation.mutate({ teamId, userId }),
    fetchTeamById: (id) => fetchTeamById(id),
    users: users || null,
    isLoadingUsers,
  };

  return <DiscordContext.Provider value={contextValue}>{children}</DiscordContext.Provider>;
};

export const useDiscord = (): DiscordContextType => {
  const context = useContext(DiscordContext);
  if (!context) {
    throw new Error('useDiscord must be used within a DiscordProvider');
  }
  return context;
};
